import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, ClipboardList, Calendar, ShieldCheck, FileText, Eye, EyeOff, Zap } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../lib/api";

const STAGES = [
  { n: 1, icon: ClipboardList, title: "Apply", body: "Tell us about yourself and upload your CV. Takes 5 minutes." },
  { n: 2, icon: Calendar, title: "14-day journey", body: "Daily lessons, reflections, and small tasks built for beginners." },
  { n: 3, icon: ShieldCheck, title: "Verification", body: "We confirm your readiness — tools, availability, communication." },
  { n: 4, icon: FileText, title: "Decision", body: "Receive an enrollment acceptance letter or a reapply notice on day 14." },
];

export default function Enroll() {
  const nav = useNavigate();
  const [track, setTrack] = useState("14-day");
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tz, setTz] = useState("America/Chicago");
  const [utm, setUtm] = useState({});
  const [showSignup, setShowSignup] = useState(false);

  useEffect(() => {
    try { setTz(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago"); } catch { /* keep default */ }
    const params = new URLSearchParams(window.location.search);
    const stored = JSON.parse(sessionStorage.getItem("dht_utm") || "null");
    setUtm({
      utm_source: params.get("utm_source") || stored?.utm_source || "",
      utm_medium: params.get("utm_medium") || stored?.utm_medium || "",
      utm_campaign: params.get("utm_campaign") || stored?.utm_campaign || "",
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/enroll/register`, {
        full_name: form.full_name, email: form.email, password: form.password, track, timezone: tz, ...utm,
      });
      localStorage.setItem("dh_candidate_token", res.data.access_token);
      localStorage.setItem("dh_candidate_email", res.data.email);
      toast.success("Account created. Welcome!");
      nav("/enroll/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to create account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FAF7EE] min-h-screen" data-testid="enroll-page">
      {/* Sub-header strip: DirectHire ENROLLMENT */}
      <div className="bg-[#FAF7EE] border-b border-[#0F2A1F]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/enroll" className="flex items-center gap-3" data-testid="enroll-logo">
            <div className="h-10 w-10 rounded-md bg-[#0F2A1F] text-white flex items-center justify-center font-bold text-sm">DH</div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-[#0F2A1F]">DirectHire</div>
              <div className="text-[10px] tracking-[0.25em] text-[#0F2A1F]/55 uppercase">Enrollment</div>
            </div>
          </Link>
          <nav className="flex items-center gap-7 text-sm text-[#0F2A1F]/80">
            <Link to="/career-pathway" className="hidden sm:inline hover:text-[#0F2A1F]" data-testid="nav-career">Career Pathway</Link>
            <Link to="/for-candidates" className="hidden sm:inline hover:text-[#0F2A1F]" data-testid="nav-graduates">Graduates</Link>
            <Link to="/enroll/login" className="hover:text-[#0F2A1F]" data-testid="nav-signin">Sign in</Link>
            <button onClick={() => { setShowSignup(true); setTimeout(() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" }), 50); }} className="rounded-md bg-[#0F2A1F] text-white px-4 py-2 text-sm font-medium hover:bg-[#163b2c]" data-testid="nav-apply-now">Apply now</button>
          </nav>
        </div>
      </div>

      {/* HERO */}
      {!showSignup && (
        <section className="py-16 md:py-24" data-testid="enroll-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">A 14-Day Enrollment Program</div>
              <h1 className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] text-[#0F2A1F] tracking-tight">
                Your path to becoming a <span className="text-[#1f5a3f]">payroll assistant</span> — guided, day by day.
              </h1>
              <p className="mt-6 text-lg text-[#0F2A1F]/65 max-w-xl leading-relaxed">
                DirectHire prepares beginners for real payroll work through a calm, structured two-week journey. Apply once. Show up daily. Get a clear decision.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button onClick={() => { setShowSignup(true); setTimeout(() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" }), 50); }} className="inline-flex items-center gap-2 rounded-md bg-[#0F2A1F] text-white px-6 py-3.5 text-sm font-medium hover:bg-[#163b2c]" data-testid="hero-start-app">
                  Start your application <ArrowRight className="h-4 w-4" />
                </button>
                <Link to="/career-pathway" className="inline-flex items-center gap-2 rounded-md bg-[#0F2A1F]/10 text-[#0F2A1F] px-6 py-3.5 text-sm font-medium hover:bg-[#0F2A1F]/15" data-testid="hero-pathway">
                  See the career pathway
                </Link>
              </div>
              <ul className="mt-8 space-y-2.5">
                {[
                  "Beginner-friendly — no prior payroll experience required",
                  "Structured 14-day program with daily progress tracking",
                  "Personalized feedback and decision letter at the end",
                  "Clear path from applicant to placed payroll assistant",
                ].map((l) => (
                  <li key={l} className="flex items-start gap-2.5 text-[#0F2A1F]/75 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-[#1f5a3f] mt-0.5 flex-shrink-0" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-5">
              <div className="relative rounded-2xl overflow-hidden bg-[#F8DCDB] p-3">
                <img
                  src="https://images.unsplash.com/photo-1573497019418-b400bb3ab074?auto=format&fit=crop&w=900&q=80"
                  alt="Payroll assistant at work"
                  className="w-full h-[460px] object-cover rounded-xl"
                />
                <div className="absolute bottom-6 left-6 bg-white rounded-lg px-4 py-3 shadow-sm flex items-center gap-3" data-testid="cohort-active-pill">
                  <div className="h-8 w-8 rounded-full bg-[#0F2A1F]/10 flex items-center justify-center text-[#0F2A1F]">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><circle cx="17" cy="11" r="3"/></svg>
                  </div>
                  <div className="text-xs">
                    <div className="tracking-[0.2em] uppercase text-[#0F2A1F]/55">Cohort Active</div>
                    <div className="font-semibold text-[#0F2A1F]">14-day program</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* STAGES — pink section */}
      {!showSignup && (
        <section className="py-20 bg-[#F8DCDB]/55" data-testid="stages-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">How it works</div>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold text-[#0F2A1F] leading-tight max-w-3xl">
              Four clear stages from application to placement.
            </h2>
            <div className="mt-12 grid md:grid-cols-4 gap-5">
              {STAGES.map((s) => (
                <div key={s.n} className="rounded-xl bg-white p-7 ring-1 ring-[#0F2A1F]/5" data-testid={`stage-${s.n}`}>
                  <div className="h-11 w-11 rounded-md bg-[#0F2A1F]/[0.06] flex items-center justify-center">
                    <s.icon className="h-5 w-5 text-[#0F2A1F]/70" />
                  </div>
                  <div className="mt-6 text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Stage {s.n}</div>
                  <div className="mt-2 text-2xl font-bold text-[#0F2A1F]">{s.title}</div>
                  <p className="mt-3 text-sm text-[#0F2A1F]/65 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SIGNUP FORM — Day 0 application */}
      <section id="apply-form" className={`py-16 md:py-24 ${showSignup ? "" : "border-t border-[#0F2A1F]/10"}`} data-testid="signup-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Day 0 — Application</div>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold text-[#0F2A1F] leading-tight">Start your application</h2>
          <p className="mt-4 text-[#0F2A1F]/65 max-w-2xl leading-relaxed">
            Pick the track that fits you. You can complete daily tasks from your laptop or phone — only 1–2 hrs/day needed.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="track-selector">
            <button
              type="button"
              onClick={() => setTrack("7-day")}
              data-testid="track-7-day"
              className={`text-left rounded-xl p-6 bg-white transition-all ring-1 ${track === "7-day" ? "ring-2 ring-[#0F2A1F] shadow-md" : "ring-[#0F2A1F]/10 hover:ring-[#0F2A1F]/30"}`}
            >
              <div className="h-9 w-9 rounded-md bg-[#0F2A1F]/[0.06] flex items-center justify-center">
                <Zap className="h-4 w-4 text-[#0F2A1F]/70" />
              </div>
              <div className="mt-5 text-xl font-bold text-[#0F2A1F]">7-Day Fast Track</div>
              <div className="mt-1 text-sm text-[#0F2A1F]/60">For motivated, fast learners</div>
              <ul className="mt-5 space-y-2 text-sm text-[#0F2A1F]/70">
                <li>✓ 1 lesson per day · 10–15 min</li>
                <li>✓ Condensed essentials</li>
                <li>✓ Same PRC certificate at the end</li>
              </ul>
            </button>
            <button
              type="button"
              onClick={() => setTrack("14-day")}
              data-testid="track-14-day"
              className={`text-left rounded-xl p-6 bg-white transition-all ring-1 relative ${track === "14-day" ? "ring-2 ring-[#0F2A1F] shadow-md" : "ring-[#0F2A1F]/10 hover:ring-[#0F2A1F]/30"}`}
            >
              <div className="flex items-start justify-between">
                <div className="h-9 w-9 rounded-md bg-[#0F2A1F]/[0.06] flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-[#0F2A1F]/70" />
                </div>
                <span className="rounded-full bg-[#F8DCDB] text-[#8b3a3a] text-[11px] font-medium px-2.5 py-1">Recommended</span>
              </div>
              <div className="mt-5 text-xl font-bold text-[#0F2A1F]">14-Day Standard</div>
              <div className="mt-1 text-sm text-[#0F2A1F]/60">Recommended for most candidates</div>
              <ul className="mt-5 space-y-2 text-sm text-[#0F2A1F]/70">
                <li>✓ 1 lesson per day · 10–15 min</li>
                <li>✓ Full curriculum + reflection</li>
                <li>✓ Same PRC certificate at the end</li>
              </ul>
            </button>
          </div>

          <form onSubmit={submit} className="mt-8 rounded-xl bg-white p-7 md:p-9 ring-1 ring-[#0F2A1F]/10 space-y-6" data-testid="signup-form">
            <div>
              <Label htmlFor="fname" className="text-[#0F2A1F]">Full name</Label>
              <Input id="fname" data-testid="signup-name" className="mt-2 bg-[#FAF7EE] border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F]" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="femail" className="text-[#0F2A1F]">Email</Label>
              <Input id="femail" type="email" data-testid="signup-email" className="mt-2 bg-[#FAF7EE] border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="fpw" className="text-[#0F2A1F]">Password</Label>
              <div className="mt-2 relative">
                <Input id="fpw" type={showPw ? "text" : "password"} data-testid="signup-password" className="bg-[#FAF7EE] border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F] pr-10" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0F2A1F]/50 hover:text-[#0F2A1F]" data-testid="signup-password-toggle" aria-label="Toggle password visibility">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-[#0F2A1F]/50">Minimum 6 characters.</p>
            </div>
            <p className="text-xs text-[#0F2A1F]/55">
              Time zone auto-detected: <strong className="text-[#0F2A1F]/80">{tz}</strong>. We use this to send daily reminders at sensible local hours.
            </p>
            <button type="submit" disabled={submitting} className="w-full rounded-md bg-[#0F2A1F] text-white py-3.5 text-sm font-medium hover:bg-[#163b2c] disabled:opacity-60" data-testid="signup-submit-btn">
              {submitting ? "Creating account…" : `Create account & start ${track === "14-day" ? "14-day" : "7-day"} program`}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-[#0F2A1F]/65" data-testid="signin-cta">
            Already have an account? <Link to="/enroll/login" className="text-[#0F2A1F] font-semibold hover:underline">Sign in</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
